<template>
  <ChartCard
    :title="title"
    note="Predicted = the model's average win chance for the side holding the stale number. Actual = how often it won (a push counts as no win)."
    :sample="waitingFor ? null : totals.graded"
    :waiting-for="waitingFor"
  >
    <!-- Band word | predicted | actual (record and coloured rate) |
         you. The you column exists only once the picker has graded picks; an
         unlinked account still gets predicted and actual. -->
    <div class="cols cols-head" :class="{ 'cols--you': yours }">
      <span></span>
      <span class="col-predicted">Predicted</span>
      <span>Actual</span>
      <span v-if="yours" class="col-you">You</span>
    </div>
    <ul class="space-y-3">
      <li v-for="band in strongestFirst" :key="band.key" class="cols band-row" :class="{ 'cols--you': yours }">
        <span class="band-label">{{ band.label }}</span>
        <span class="col-predicted band-rate">{{ band.graded ? percent(band.predictedWinRate) || '—' : '—' }}</span>
        <span class="col-actual">
          <span class="band-record">
            <template v-if="band.graded">{{ band.wins }}/{{ band.graded }}</template>
            <template v-else>—</template>
          </span>
          <!-- Coloured on actual vs predicted: green at or above, red below,
               grey on too few games to say. -->
          <span v-if="band.graded" class="band-rate" :class="`rate--${bandOutcomeTone(band)}`">{{ percent(band.winRate) }}</span>
          <!-- Its own line: beside the record it pushed the rate onto one. -->
          <small v-if="band.pushes" class="band-push band-push--line">+{{ band.pushes }} push{{ band.pushes === 1 ? '' : 'es' }}</small>
        </span>
        <span v-if="yours" class="col-you band-record">
          <template v-if="yoursByKey[band.key].sample">{{ yoursByKey[band.key].wins }}/{{ yoursByKey[band.key].sample }}</template>
          <template v-else>—</template>
          <small v-if="yoursByKey[band.key].pushes" class="band-push">+{{ yoursByKey[band.key].pushes }} push{{ yoursByKey[band.key].pushes === 1 ? '' : 'es' }}</small>
        </span>
      </li>
    </ul>
    <p class="totals">
      All bands: predicted {{ percent(totals.predictedWinRate) || '—' }}
      · actual {{ percent(totals.winRate) || '—' }}
      · {{ totals.graded }} game{{ totals.graded === 1 ? '' : 's' }}
    </p>
    <p v-if="yours" class="yours-total">
      Your picks by line value: {{ yoursTotal || 'none rated' }}
      <small v-if="yours.unrated" class="yours-unrated">· {{ yours.unrated }} unrated</small>
    </p>
  </ChartCard>
</template>

<script>
import ChartCard from "./ChartCard.vue";
import { bandOutcomeTone, edgeBandTotals } from "@/utils/history.js";

export default {
  name: "EdgeReportCard",
  components: { ChartCard },
  props: {
    title: { type: String, default: "Edge report card" },
    // `edgeBandSummaries` output: one row per rung of `EDGE_BAND_ORDER`. The
    // card reads `graded` (every graded game, pushes included), `wins`,
    // `pushes`, `winRate` (a push is a non-win) and `predictedWinRate` (the
    // mean edge-side `winProbability` over those same games).
    bands: { type: Array, default: () => [] },
    // `yourPickBands` output: the picker's own graded picks per rung, banded
    // on the picked side's line value, with a count of picks that could not be
    // rated. Null drops the you column (an unlinked account).
    yours: { type: Object, default: null },
    // When there is not yet a scored, spread-carrying game to grade.
    hasData: { type: Boolean, default: false },
    waitLabel: {
      type: String,
      default:
        "No scored games with a frozen line yet — this fills in once the 2026 season starts and the first edges settle.",
    },
  },
  computed: {
    // `bands` arrives in ladder order (Flat → Strong); read it top-down from
    // the strongest rung so the band the thesis cares about leads.
    strongestFirst() {
      return [...this.bands].reverse();
    },
    // "3 Solid · 1 Slight · 6 Flat" -- strongest first, empty rungs left out.
    yoursTotal() {
      if (!this.yours) return "";
      return [...this.yours.bands]
        .reverse()
        .filter((b) => b.count)
        .map((b) => `${b.count} ${b.label}`)
        .join(" · ");
    },
    yoursByKey() {
      const empty = { wins: 0, sample: 0, pushes: 0 };
      const rows = (this.yours && this.yours.bands) || [];
      return Object.fromEntries(this.bands.map((band) => [band.key, rows.find((b) => b.key === band.key) || empty]));
    },
    totals() {
      return edgeBandTotals(this.bands);
    },
    waitingFor() {
      return this.hasData && this.totals.graded > 0 ? "" : this.waitLabel;
    },
  },
  methods: {
    bandOutcomeTone,
    percent(value) {
      return value == null ? "" : `${value.toFixed(0)}%`;
    },
  },
};
</script>

<style scoped>
.band-label { color: #94a3b8; font-size: 0.75rem; font-weight: 700; }
.band-record { min-width: 0; color: #e2e8f0; font-size: 0.75rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.band-push { margin-left: 0.25rem; color: #64748b; font-size: 0.65rem; font-weight: 700; white-space: nowrap; }
.band-rate { color: #cbd5e1; font-size: 0.75rem; font-weight: 800; font-variant-numeric: tabular-nums; }
/* Band word | predicted | actual | you. Predicted and you are fixed widths so
   their figures line up; actual takes the rest. At 375px with the you column
   that is still ~95px: "12/20 60%" on one line, any push note on the next. */
.cols { display: grid; grid-template-columns: 3.25rem 4.25rem minmax(0, 1fr); column-gap: 0.75rem; align-items: baseline; }
.cols--you { grid-template-columns: 3.25rem 4.25rem minmax(0, 1fr) 4rem; }
/* Matches the Results page's column labels. */
.cols-head { margin-bottom: 0.45rem; color: #475569; font-size: 0.6rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.col-predicted { text-align: right; }
.col-actual { display: flex; min-width: 0; flex-wrap: wrap; align-items: baseline; justify-content: space-between; column-gap: 0.4rem; }
.band-push--line { flex-basis: 100%; margin-left: 0; }
.col-you { text-align: right; }
.cols .col-you.band-record { white-space: normal; }
.totals { margin-top: 0.85rem; border-top: 1px solid rgb(255 255 255 / 0.06); padding-top: 0.6rem; color: #94a3b8; font-size: 0.7rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.yours-total { margin-top: 0.35rem; color: #94a3b8; font-size: 0.7rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.yours-unrated { color: #64748b; font-size: 0.65rem; font-weight: 700; }
.rate--good { color: #34ef93; }
.rate--bad { color: #fb7185; }
.rate--neutral { color: #94a3b8; }
</style>
